// askSmart-ui/src/pages/TOSP/portal/tenantGroc.ts
//
// Sales PSF and GROC — the two ratios a property manager judges a tenancy by.
//
//   Sales PSF = sales / COMMERCIAL area
//   GROC      = occupancy cost excluding HST / sales
//
// GROC is the Gross Rent and Occupancy Cost Ratio, and the cost side is taken
// from the tenant's own statement rather than recalculated here: the statement
// already works out rent from the lease, taxes from the roll budgets, both BMVA
// charges and percentage rent, and a second implementation of any of that would
// drift from it the first time a rule changed. So this module windows the
// statement and divides — nothing more.
//
// HST is excluded. It is a tax collected on the charges, not a cost of
// occupying the premises, and including it would overstate every ratio by 13%.
//
// THE WINDOW MOVES WITH THE SALES, NOT WITH THE CALENDAR: twelve months ending
// at the last month the tenant actually reported. A window ending "last month"
// would silently divide a full year of rent by eleven months of sales the
// moment a tenant was late, and the ratio would lurch every time somebody filed
// — the figure would be at its worst precisely when the data was least complete.

import {
  BudgetYear,
  COMMERCIAL_SPACE_TYPE,
  isOccupancyActive,
  Tenant,
  TaxRollRecord,
  TospConfiguration,
  Unit,
} from "../types";
import { sameText } from "../validation";
import {
  expenseYearFromSnapshot,
  publishedStatement,
  tenantExpenseYear,
} from "./tenantExpenses";
import { YearMonth } from "./portalSummary";
import { TenantSalesMonth } from "./tenantSales";

export interface TenantGroc {
  /** Twelve months ending at the last month reported, inclusive. */
  from: YearMonth;
  to: YearMonth;
  /** Sales reported inside the window. Months never reported count as nothing. */
  sales: number;
  /** How many of the twelve the tenant actually filed. */
  monthsReported: number;
  /** Commercial area only — what the ratio is quoted against. */
  commercialSqFt: number | null;
  salesPsf: number | null;
  /** Everything the statement charges across the window, less HST. */
  cost: number;
  /** cost / sales. Null when there are no sales to divide by. */
  groc: number | null;
  /**
   * True while any statement line can't be calculated yet — CAMS, until the
   * budget's per-tenant allocation is built. The figure is then UNDERSTATED,
   * and says so rather than passing as complete.
   */
  incomplete: boolean;
  /** Which lines are missing, for the tooltip. */
  missingLabels: string[];
}

/**
 * Where a tenant's Sales PSF sits against the rest of the portfolio.
 *
 * The same three tones the breakpoint bars use, so the colours mean the same
 * thing wherever they appear: green is good, red wants attention.
 */
export type PsfBand = "green" | "amber" | "red";

export interface PsfQuartiles {
  /** Bottom quarter ends here; at or below it is red. */
  lower: number;
  /** Top quarter starts here; at or above it is green. */
  upper: number;
  /** How many tenants the quartiles were worked out from. */
  count: number;
}

/**
 * The two cut-offs, from every tenant that HAS a figure.
 *
 * Worked out across the whole portfolio rather than the rows on screen: a
 * colour that changed when the list was sorted or filtered would say something
 * different every time it was looked at, and the key beside the column would be
 * describing a set nobody can see.
 *
 * Null below four tenants — quarters of three or fewer are not quarters, and a
 * portfolio that small is read directly rather than ranked.
 */
export function psfQuartiles(values: (number | null | undefined)[]): PsfQuartiles | null {
  const known = values.filter((v): v is number => v !== null && v !== undefined).sort((a, b) => a - b);
  if (known.length < 4) return null;

  // Nearest-rank, which lands on a tenant's own figure rather than between two
  // of them — the cut-off is then a number somebody actually reported.
  const at = (fraction: number): number =>
    known[Math.min(known.length - 1, Math.max(0, Math.ceil(fraction * known.length) - 1))];

  return { lower: at(0.25), upper: at(0.75), count: known.length };
}

/** Which quarter a figure falls in. Null when there is nothing to rank against. */
export function psfBand(
  value: number | null | undefined,
  quartiles: PsfQuartiles | null,
): PsfBand | null {
  if (value === null || value === undefined || !quartiles) return null;
  if (value >= quartiles.upper) return "green";
  return value <= quartiles.lower ? "red" : "amber";
}

export interface GrocInputs {
  tenant: Tenant;
  unitById: (unitId: string) => Unit | undefined;
  configuration: TospConfiguration;
  taxRolls: TaxRollRecord[];
  sales: TenantSalesMonth[];
  /** Needed so a published year reads its frozen statement rather than a live one. */
  budgets: Pick<BudgetYear, "year" | "history" | "publishedSnapshot">[];
  /**
   * One tenancy year of CAM figures, for a year that has a budget but is not
   * published. Worked out once for the whole portfolio by the caller.
   */
  camMonths?: (year: number, tenantId: string) => number[] | undefined;
  /** Length of the window. Twelve unless something else is being asked. */
  months?: number;
}

/** The twelve (year, month) pairs ending at `end`, oldest first. */
function windowMonths(end: YearMonth, months: number): YearMonth[] {
  const out: YearMonth[] = [];
  for (let back = months - 1; back >= 0; back -= 1) {
    // Month arithmetic in a single index avoids a Date and its timezone.
    const index = end.year * 12 + (end.month - 1) - back;
    out.push({ year: Math.floor(index / 12), month: (index % 12) + 1 });
  }
  return out;
}

/**
 * The tenant's commercial area, counted once per UNIT.
 *
 * Two leased spaces can point at one unit — a lease that names the ground floor
 * twice, or a renewal recorded alongside the original — and adding the area
 * twice would halve the ratio. Live leases only: space a tenant has given back
 * is not space they trade from.
 */
export function commercialSqFt(
  tenant: Tenant,
  unitById: (unitId: string) => Unit | undefined,
): number | null {
  const counted = new Set<string>();
  let total: number | null = null;

  for (const lease of (tenant.occupancies || []).filter(isOccupancyActive)) {
    for (const space of lease.leasedSpaces || []) {
      if (!space.unitId || counted.has(space.unitId)) continue;
      const unit = unitById(space.unitId);
      if (!unit || !sameText(unit.spaceType, COMMERCIAL_SPACE_TYPE)) continue;
      counted.add(space.unitId);
      if (unit.occupancySqFt != null) total = (total ?? 0) + unit.occupancySqFt;
    }
  }

  return total;
}

/**
 * Both ratios for one tenant, or null when they have never reported sales.
 *
 * Null rather than zero: a tenant with no sales has no ratio, and 0% would read
 * as a tenancy costing nothing rather than one nobody has figures for.
 */
export function tenantGroc({
  tenant,
  unitById,
  configuration,
  taxRolls,
  sales,
  budgets,
  camMonths,
  months = 12,
}: GrocInputs): TenantGroc | null {
  const reported = sales.filter((entry) => entry.tenantId === tenant.id);
  if (reported.length === 0) return null;

  // The anchor: the last month filed, wherever the calendar happens to be.
  const end = reported.reduce<YearMonth>(
    (latest, entry) =>
      entry.year * 12 + entry.month > latest.year * 12 + latest.month
        ? { year: entry.year, month: entry.month }
        : latest,
    { year: reported[0].year, month: reported[0].month },
  );

  const window = windowMonths(end, months);

  let salesTotal = 0;
  let monthsReported = 0;
  for (const { year, month } of window) {
    const found = reported.find((entry) => entry.year === year && entry.month === month);
    if (!found) continue;
    salesTotal += Number(found.grossSales) || 0;
    monthsReported += 1;
  }

  // The statement is built a calendar year at a time, so the window spans two
  // of them at most. Each is worked out once and then read month by month.
  const years = [...new Set(window.map((m) => m.year))];
  const statements = new Map(
    years.map((year) => {
      // A PUBLISHED YEAR USES ITS FROZEN STATEMENT. The ratio is read beside
      // the figures a tenant was actually billed, so working it out from the
      // leases instead would put a different number next to the same tenancy
      // on two screens — and the frozen one is the one that was agreed.
      const frozen = publishedStatement(budgets, tenant.id, year);
      return [
        year,
        frozen
          ? expenseYearFromSnapshot(frozen, year)
          : tenantExpenseYear({
              tenant,
              year,
              unitById,
              configuration,
              taxRolls,
              sales,
              // The same CAM figures the statement preview shows, so the ratio
              // and the statement it is read beside cannot differ. Passed in
              // rather than computed here: the caller works the allocation out
              // once for the whole portfolio, not once per tenancy.
              cams: camMonths?.(year, tenant.id),
            }),
      ] as const;
    }),
  );

  let cost = 0;
  const missing = new Set<string>();
  for (const { year, month } of window) {
    const statement = statements.get(year);
    if (!statement) continue;
    // HST is excluded by definition; `total` is the sum of the rest and would
    // double every charge.
    for (const line of statement.lines) {
      if (line.key === "hst" || line.key === "total") continue;
      cost += line.months[month - 1] ?? 0;
    }

    // THE UNDERSTATEMENT IS NOW AN ABSENCE, not a flag on a row.
    //
    // CAM only exists once a budget is published, so a year that has no cams
    // line has no CAM figure in existence — and a ratio of occupancy cost that
    // leaves CAM out is short. Recorded so the GROC cell can say so, which is
    // the same signal the old "not yet calculated" row carried, without a
    // statement row describing the state of the software.
    if (!statement.lines.some((line) => line.key === "cams")) missing.add("CAMS");
  }

  const area = commercialSqFt(tenant, unitById);

  return {
    from: window[0],
    to: end,
    sales: salesTotal,
    monthsReported,
    commercialSqFt: area,
    salesPsf: area && salesTotal ? salesTotal / area : null,
    cost,
    groc: salesTotal > 0 ? cost / salesTotal : null,
    incomplete: missing.size > 0,
    missingLabels: [...missing],
  };
}
